import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { defaultFormikConfig, formikFieldProps } from "../framework/formUtils";
import {
    ReportTemplateDto,
    ReportTemplateSaveDto,
} from "../../models/reporting/reportTemplate";
import { useFormik } from "formik";
import * as yup from "yup";
import { EditDialog, ExposedFunctions } from "../framework/editDialog";
import TextField from "@mui/material/TextField";

interface Props {
    template: ReportTemplateDto;
    open: boolean;
    onClose: () => void;
    onSubmit: (dto: Partial<ReportTemplateSaveDto>) => Promise<any>;
    isEdit?: boolean;
}

export const ReportTemplateDialog = ({
    template,
    open,
    onClose,
    onSubmit,
    isEdit,
}: Props) => {
    const { t } = useTranslation();
    const dialogRef = useRef<ExposedFunctions>();

    const saveItem: Partial<ReportTemplateSaveDto> = useMemo(
        () => ({
            name: "",
        }),
        []
    );

    const handleSubmit = async(dto: Partial<ReportTemplateSaveDto>) => {
        try {
            await onSubmit(dto);
            formik.setSubmitting(false);
            dialogRef.current?.setInitialFormDataFromForm();
            onClose();
        } catch (error) {
            formik.setSubmitting(false);
        }
    };

    const formik = useFormik<Partial<ReportTemplateSaveDto>>({
        ...defaultFormikConfig,
        enableReinitialize: true,
        initialValues: saveItem,
        validationSchema: yup.object({
            name: yup
                .string()
                .required(
                    t("validation.fieldIsRequired", {
                        field: t("common.name"),
                    })
                )
                .max(50),
        }),
        onSubmit: handleSubmit,
    });

    const getTitleText = () => {
        if (isEdit) return t("reportTemplate.rename", { name: template?.name });
        return template?.name ?? t("reportTemplate.newReport");
    };

    return (
        <EditDialog
            ref={dialogRef}
            title={getTitleText()}
            open={open}
            onClose={onClose}
            formik={formik}
            maxWidth="xs"
            fullWidth
            enableDefaultActions
            body={
                <TextField
                    required
                    label={t("common.name")}
                    fullWidth
                    {...formikFieldProps(formik, "name")}
                />
            }
        />
    );
};
